import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from 'clsx';
import React, { useMemo } from 'react';
import { Bubble, Scatter } from 'react-chartjs-2';
import { useMeasure } from 'react-use';
import { chartColorPallet as DEFAULT_COLORS } from '@/config/colorPallets/universalDesignSystem';
import Header from '../../misc/Header';
import { getClickPositionFromEvent } from '@/utils/dom';
import { deleteUndefinedValues } from '@/utils/objects';
import { selectEditingArea, getContextEditingSectionAttr } from '@/components/Editor/hooks/useEditingAreas';
import { hasAnySelectionData } from '../../../utils/selections';
import { extractDataLabelProperty, getSectionCanvasId } from '../utils';
import useSubscribeWidgetResults from '../../hooks/useSubscribeWidgetResults';
import StyleVariables, { getSectionAttr, getSectionAttrSelector } from '../../../misc/StyleVariables';
import { getSuggestedChartFraming, isCategorical, overrideConfig } from './utils';
import { usePointStyles } from './stylesTheme';
import useSelections from '@/components/widget/misc/useSelections';
import useSelectionLabels from '@/components/widget/misc/useSelectionLabels';
import useSelectionLegends from '@/components/widget/misc/useSelectionLegends';
import useData from './useData';
import useChartActions from '../useChartActions';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_MAX_HEIGHT_RATIO = 0.99;

const PointChart = _ref => {
  let {
    widgetId,
    sectionId,
    reportingWidgetId,
    elementId,
    editing = false,
    chartType,
    hasLoadedDeps,
    settings,
    selectionConfig,
    labelsSelectionConfig,
    legendsSelectionConfig,
    legendsSelectionSkipCount,
    Loading,
    config: defaultConfig
  } = _ref;
  const config = useMemo(() => overrideConfig(defaultConfig), [defaultConfig]);
  const [containerEl, {
    width: containerWidth
  }] = useMeasure();
  const {
    themeStyles,
    themeCtx: {
      colorPallet: defaultColorPallet = DEFAULT_COLORS
    }
  } = usePointStyles(config);
  const {
    hasLegend,
    selections,
    labelsSelections,
    legendsSelections,
    seriesOptions = [],
    globalSeriesOptions,
    colorPallet = defaultColorPallet,
    groupOptions = [],
    collectionOptions = [],
    selectionsStrategy = 'default',
    selectionsLabeled = true,
    animation = {}
  } = config;
  const xAxisTemplate = config.xAxisTemplate || (globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.xAxisTemplate);
  const yAxisTemplate = config.yAxisTemplate || (globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.yAxisTemplate);
  const pointTemplate = config.pointTemplate || (globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.pointTemplate);
  const results = useSubscribeWidgetResults(reportingWidgetId); // Apply selections to results

  const {
    selectionResults,
    resultsUpdated
  } = useSelections({
    results,
    selections: selections || '',
    selectionsLabeled,
    selectionConfig
  });
  const {
    selectionLabels
  } = useSelectionLabels({
    results,
    resultsUpdated,
    selectionsLabeled,
    labelsSelectionConfig,
    labelsSelections,
    labelsTemplate: (globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.labelsTemplate) || xAxisTemplate
  });
  const {
    selectionLegends
  } = useSelectionLegends({
    results,
    resultsUpdated,
    legendsSelectionConfig,
    legendsSelections,
    skipResultsCount: legendsSelectionSkipCount
  }); // Combine results with rendering styles

  const {
    chartData,
    dataBoundaries,
    axisTemplate
  } = useData({
    selectionResults,
    selectionLabels,
    selectionLegends,
    seriesOptions,
    globalSeriesOptions,
    groupOptions,
    collectionOptions,
    colorPallet,
    isCategorical: isCategorical(selectionsStrategy, results),
    seriesMinimumLength: selectionConfig.seriesMinimumLength || 2,
    chartType,
    pointTemplate,
    xAxisTemplate,
    yAxisTemplate,
    legends: config.legends,
    globalTrendline: config.trendline,
    xScaleMin: config.xScaleMin,
    xScaleMax: config.xScaleMax,
    themeStyles
  });
  const {
    xAxisTicksCallback,
    yAxisTicksCallback,
    openLegendsContextEditor
  } = useChartActions({
    xAxisTemplate: xAxisTemplate ?? axisTemplate,
    yAxisTemplate: yAxisTemplate ?? axisTemplate
  }); // Data & Loading States

  const hasNoData = !hasAnySelectionData(selectionResults);
  const isLoading = !results.length;
  const hasDrawableData = Boolean(chartData.datasets.length); // Create chart options

  const options = useMemo(() => {
    var _config$styles;

    const animation = config.animation || {};
    const chartTitle = config.chartTitle ? {
      display: true,
      text: config.chartTitle,
      font: {
        family: themeStyles.titleFontFamily,
        size: themeStyles.titleFontSize,
        lineHeight: themeStyles.titleLineHeight
      },
      color: themeStyles.titleColor
    } : {}; // Chart Framing

    const suggestedChartFraming = getSuggestedChartFraming(config, dataBoundaries);
    const legendPosition = config.legendPosition || 'bottom';
    const axisFont = {
      font: {
        family: themeStyles.chartAxisFontFamily,
        size: themeStyles.chartAxisFontSize,
        weight: themeStyles.chartAxisFontWeight
      },
      color: themeStyles.chartAxisFontColor
    };
    const scales = {
      x: deleteUndefinedValues({
        suggestedMin: suggestedChartFraming.xMin,
        suggestedMax: suggestedChartFraming.xMax,
        min: globalSeriesOptions !== null && globalSeriesOptions !== void 0 && globalSeriesOptions.forceZeroBaseline ? 0 : config.xScaleMin,
        max: config.xScaleMax,
        title: config.xAxisTitle ? _objectSpread({
          display: config.xAxisDisplay ?? true,
          text: config.xAxisTitle
        }, axisFont) : undefined,
        grid: {
          color: themeStyles.xAxisGridVisible ? themeStyles.xAxisGridColor : 'transparent',
          drawTicks: false,
          lineWidth: themeStyles.xAxisGridBorderWidth ?? 1
        },
        border: {
          dash: themeStyles.xAxisGridBorderDash ?? [5, 5],
          display: false
        },
        ticks: _objectSpread(_objectSpread({}, axisFont), {}, {
          display: config.xAxisDisplay ?? true,
          padding: themeStyles.xAxisTicksPadding,
          callback: xAxisTicksCallback
        })
      }),
      y: deleteUndefinedValues({
        suggestedMin: suggestedChartFraming.yMin,
        suggestedMax: suggestedChartFraming.yMax,
        min: globalSeriesOptions !== null && globalSeriesOptions !== void 0 && globalSeriesOptions.forceZeroBaseline ? 0 : config.yScaleMin,
        max: config.yScaleMax,
        title: config.yAxisTitle ? _objectSpread({
          display: config.yAxisDisplay ?? true,
          text: config.yAxisTitle
        }, axisFont) : undefined,
        grid: {
          color: themeStyles.yAxisGridVisible ? themeStyles.yAxisGridColor : 'transparent',
          drawTicks: false,
          lineWidth: themeStyles.yAxisGridBorderWidth ?? 1
        },
        border: {
          dash: themeStyles.yAxisGridBorderDash ?? [5, 5],
          display: false
        },
        ticks: _objectSpread(_objectSpread({}, axisFont), {}, {
          display: config.yAxisDisplay ?? true,
          padding: themeStyles.yAxisTicksPadding,
          callback: yAxisTicksCallback
        })
      })
    };
    const isAnimationDisabled = animation.enabled !== undefined && !animation.enabled;
    return {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 1,
      scales,
      layout: {
        autoPadding: false
      },
      animation: _objectSpread(_objectSpread({}, deleteUndefinedValues(animation)), {}, {
        duration: isAnimationDisabled ? 0 : animation.duration
      }),
      plugins: {
        title: chartTitle,
        datalabels: {
          display: true,
          borderRadius: themeStyles.chartDataLabelBorderRadius,
          color: context => extractDataLabelProperty(context, 'color', themeStyles.chartDataLabelColor),
          align: context => extractDataLabelProperty(context, 'align', 'center'),
          anchor: context => extractDataLabelProperty(context, 'anchor', 'end'),
          rotation: context => extractDataLabelProperty(context, 'rotation', 0),
          clamp: context => extractDataLabelProperty(context, 'clamp', true),
          formatter: function (value, context) {
            var _context$dataset$data;

            // Hide labels if not enabled or with value 0
            const dataLabel = (_context$dataset$data = context.dataset.datalabels) === null || _context$dataset$data === void 0 ? void 0 : _context$dataset$data[context.dataIndex];
            if (!(dataLabel !== null && dataLabel !== void 0 && dataLabel.display) || value === 0) return '';
            return dataLabel.template ?? value;
          },
          backgroundColor: function (context) {
            var _context$dataset$data2;

            // Hide labels if not enabled or with value 0
            const dataLabel = (_context$dataset$data2 = context.dataset.datalabels) === null || _context$dataset$data2 === void 0 ? void 0 : _context$dataset$data2[context.dataIndex];
            if (!(dataLabel !== null && dataLabel !== void 0 && dataLabel.display)) return '';
            return themeStyles.chartDataLabelBackgroundColor;
          }
        },
        legend: _objectSpread({
          align: ['top', 'bottom'].includes(legendPosition) ? 'start' : undefined,
          position: legendPosition,
          display: hasLegend ?? false,
          labels: {
            pointStyle: 'rectRounded',
            usePointStyle: true,
            font: {
              family: themeStyles.legendFontFamily,
              size: themeStyles.legendFontSize,
              lineHeight: themeStyles.legendLineHeight
            },
            color: themeStyles.legendColor,
            // Remove unwanted datasets from legend
            filter: item => item.text !== undefined
          }
        }, editing && {
          onClick: e => openLegendsContextEditor(e, widgetId, sectionId)
        }),
        tooltip: {
          enabled: true,
          usePointStyle: true,
          cornerRadius: 0,
          callbacks: {
            title: _ref2 => {
              let [item] = _ref2;
              if (!item.dataset.titles) return;
              return item.dataset.titles[item.dataIndex] || item.dataset.label;
            },
            label: item => {
              return `${(item.dataset.templates || [])[item.dataIndex]}` || `(${item.parsed.x}, ${item.parsed.y}, ${item.parsed.r})` // Chart.js default
              ;
            }
          }
        },
        legendPadding: {
          padding: 25 // Custom padding between legend and chart

        },
        activeDatasetHighlightPlugin: {
          enable: editing,
          dimOpacity: 0.15,
          // Opacity for non-hovered lines.
          hoverCursor: 'pointer',
          extraElements: [`[${getContextEditingSectionAttr()}]`, '[data-floating-ui-portal]']
        },
        customCanvasBackgroundColor: {
          color: (config === null || config === void 0 ? void 0 : (_config$styles = config.styles) === null || _config$styles === void 0 ? void 0 : _config$styles.backgroundColor) || themeStyles.chartBackgroundColor
        },
        axisLabelClickPlugin: {
          enabled: true,
          onAxisClick: (event, position) => handleAxisClick({
            widgetId,
            sectionId
          }, event, position)
        }
      },
      onClick: editing ? (evt, elements) => setEditingAreas({
        widgetId,
        sectionId
      }, evt, elements) : undefined
    };
  }, [widgetId, sectionId, editing, hasLegend, config, dataBoundaries, globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.forceZeroBaseline, themeStyles, xAxisTicksCallback, yAxisTicksCallback, openLegendsContextEditor]);
  const chartRenderingHash = Object.entries(animation).map(_ref3 => {
    let [key, value] = _ref3;
    return `${key}:${value}`;
  }).join('.');
  const isShowingLoader = !hasLoadedDeps || !hasDrawableData;
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread(_objectSpread({
    ref: containerEl
  }, elementId && {
    id: elementId
  }), {}, {
    className: styles.container
  }, getSectionAttr(widgetId, sectionId)), {}, {
    children: [/*#__PURE__*/_jsx(Header, {
      editing: editing,
      widgetId: widgetId,
      sectionId: sectionId,
      config: config,
      className: styles.header
    }), isLoading || hasNoData ? Loading ? /*#__PURE__*/_jsx(Loading, {
      editing: editing
    }) : /*#__PURE__*/_jsx(_Fragment, {}) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [isShowingLoader && Loading ? /*#__PURE__*/_jsx(Loading, {
        editing: editing
      }) : /*#__PURE__*/_jsx(_Fragment, {}), /*#__PURE__*/_jsx("div", {
        className: clsx(styles.main, isShowingLoader ? styles['main--hide'] : ''),
        children: hasLoadedDeps ? chartType === 'bubble' ? /*#__PURE__*/_jsx(Bubble, {
          id: getSectionCanvasId(widgetId, sectionId),
          data: chartData // @ts-expect-error dataset types mismatch
          ,
          options: options
        }, chartRenderingHash) : /*#__PURE__*/_jsx(Scatter, {
          id: getSectionCanvasId(widgetId, sectionId),
          data: chartData // @ts-expect-error dataset types mismatch
          ,
          options: options
        }, chartRenderingHash) : /*#__PURE__*/_jsx(_Fragment, {})
      })]
    }), /*#__PURE__*/_jsx(StyleVariables, {
      selector: getSectionAttrSelector(widgetId, sectionId),
      data: applyMaxHeightFallback(_objectSpread(_objectSpread({}, settings.styles), config.styles || {}), containerWidth * DEFAULT_MAX_HEIGHT_RATIO)
    })]
  }));
};

export default PointChart; // Add a necessary fallback max height (based on width)
// to the container, so that the chart does not constaintly
// rerender to fill an ever expanding height

function applyMaxHeightFallback(settingStyles, maxHeight) {
  if (settingStyles.maxHeight === undefined && maxHeight > 0) {
    settingStyles.maxHeight = `${Math.round(maxHeight)}px`;
  }

  return settingStyles;
}

// Apply user selections of an chart
// item/point as the active editing area
function setEditingAreas(_ref4, evt, elements) {
  var _evt$chart, _evt$chart$data;

  let {
    widgetId = '',
    sectionId
  } = _ref4;
  // Default to section only selection
  const editAreaSelection = {
    widgetId,
    sectionId,
    sectionEditingScope: 'selections',
    editingAreaMeta: ''
  };
  const metaData = {
    selectionIndex: -1,
    collectionIndex: -1
  };
  const datasets = (evt === null || evt === void 0 ? void 0 : (_evt$chart = evt.chart) === null || _evt$chart === void 0 ? void 0 : (_evt$chart$data = _evt$chart.data) === null || _evt$chart$data === void 0 ? void 0 : _evt$chart$data.datasets) || [];
  const item = elements && elements[0] || null;

  if (!item || typeof item.datasetIndex !== 'number' || typeof item.index !== 'number') {
    handleEmptyAreaClick({
      widgetId,
      sectionId
    }, evt);
    return;
  } // If empty or only one dataset, treat as empty area click


  if (datasets.length <= 1) {
    handleEmptyAreaClick({
      widgetId,
      sectionId
    }, evt);
    return;
  }

  metaData.collectionIndex = item.datasetIndex;
  const dataset = datasets[item.datasetIndex] || {
    data: []
  }; // Get selection index depending on selection type

  let selectionIndex = -1; // @ts-ignore

  if (Array.isArray(dataset.selectionIndexes)) {
    // @ts-ignore
    selectionIndex = dataset.selectionIndexes[item.index]; // @ts-ignore
  } else if (typeof dataset.selectionIndex === 'number') {
    // @ts-ignore
    selectionIndex = dataset.selectionIndex;
  }

  if (selectionIndex > -1) {
    editAreaSelection.clickPosition = getClickPositionFromEvent(evt.native);
    editAreaSelection.sectionEditingArea = `item-${selectionIndex}`;
    metaData.selectionIndex = selectionIndex;
  } // Get selection index depending on selection type


  let selection = '';

  if (Array.isArray(dataset.selectionIndexes)) {
    selection = dataset.selections[item.index];
  } else if (typeof dataset.selection === 'string') {
    selection = dataset.selection;
  }

  if (selection) {
    metaData.selection = selection;
  }

  if (Object.keys(metaData).length) {
    editAreaSelection.editingAreaMeta = JSON.stringify(metaData);
  } // Send selection


  selectEditingArea(editAreaSelection);
} // Handles clicks in empty chart areas.


const handleEmptyAreaClick = (context, event) => {
  selectEditingArea({
    widgetId: context.widgetId,
    sectionId: context.sectionId,
    sectionEditingScope: 'chart',
    sectionEditingArea: 'chart',
    editingAreaMeta: '',
    clickPosition: getClickPositionFromEvent(event.native)
  });
};

const handleAxisClick = (context, event, position) => {
  selectEditingArea({
    widgetId: context.widgetId,
    sectionId: context.sectionId,
    sectionEditingScope: 'chart-axes',
    sectionEditingArea: 'chart-axes',
    editingAreaMeta: JSON.stringify({
      axis: position.axis
    }),
    clickPosition: getClickPositionFromEvent(event.native)
  });
};